<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, type Ref } from "vue";
import type { DropdownItem, UIColor, Value } from "../../..";
import { isEqualItem } from "../../helpers.js";
import TickBox from "../TickBox/TickBox.vue";
import Copy from "../Copy/Copy.vue";

const props = defineProps<{
  label: string;
  value: Value;
  selected?: boolean;
  disabled?: boolean;
}>();

const colorForeground = inject<UIColor>("colorForeground", "--color-primary");
const multiple = inject<boolean>("multiple", false);
const dropdownModel = inject<Ref<Value[] | undefined>>("model")!;
const items = inject<Ref<DropdownItem<Value>[]>>("items")!;

onMounted(() => {
  items.value = [...items.value, { value: props.value, label: props.label }];
});

onUnmounted(() => {
  const itemsCopy = [...items.value];
  const index = itemsCopy.findIndex((item) =>
    isEqualItem(props.value, item.value)
  );

  if (index !== -1) {
    itemsCopy.splice(index, 1);
    items.value = [...itemsCopy];
  }
});

const checked = computed({
  get() {
    return !!dropdownModel.value?.includes(props.value);
  },
  set(selected: boolean) {
    const model = [...(dropdownModel.value ?? [])];
    const index = model.findIndex((item) => isEqualItem(item, props.value));

    if (selected && index === -1) model.push(props.value);
    else if (!selected && index !== -1) model.splice(index, 1);

    dropdownModel.value = model;
  },
});
</script>

<template>
  <TickBox
    v-if="multiple"
    v-model="checked"
    :color="colorForeground"
    :disabled="!!disabled"
  >
    <template #labelAfter>
      <Copy type="label" size="small" :color="colorForeground">
        {{ label }}
      </Copy>
    </template>
  </TickBox>
  <option v-else :value="value" :selected="!!selected" :disabled="!!disabled">
    {{ label }}
  </option>
</template>
